// Uygulamanın gerçek arka plan -> ön plan dönüşünü tek bir olayda birleştirir.
// iOS WKWebView aynı dönüşte focus + pageshow + visibilitychange üretebilir.
// İlk sayfa açılışı veya sıradan sayfa geçişleri resume sayılmaz.
const RESUME_GUARD_MS = 1800;
const MIN_INACTIVE_MS = 250;

const subscribers = new Map();
let listenersInstalled = false;
let inactiveSince = 0;
let inactiveObserved = false;
let lastResumeAt = 0;
let nextSubscriberId = 1;
const pendingTimers = new Set();

const clearPendingTimers = () => {
  pendingTimers.forEach((timerId) => clearTimeout(timerId));
  pendingTimers.clear();
};

const markInactive = () => {
  if (!inactiveObserved) inactiveSince = Date.now();
  inactiveObserved = true;
};

const runSubscriber = (entry) => {
  try {
    const result = entry.callback();
    if (result && typeof result.catch === "function") {
      result.catch((error) => console.warn("app resume callback error:", error));
    }
  } catch (error) {
    console.warn("app resume callback error:", error);
  }
};

const emitResume = ({ force = false } = {}) => {
  if (typeof document !== "undefined" && document.hidden) return;

  const now = Date.now();
  const wasActuallyInactive = inactiveObserved && (now - inactiveSince >= MIN_INACTIVE_MS);
  if (!force && !wasActuallyInactive) return;
  if (now - lastResumeAt < RESUME_GUARD_MS) return;

  lastResumeAt = now;
  inactiveObserved = false;
  inactiveSince = 0;
  clearPendingTimers();

  // Aboneler aynı anda sunucuya yük bindirmesin; en geç 1 saniye içinde
  // çalışırlar. Kullanıcı açısından yenileme yine anlık hissedilir.
  Array.from(subscribers.values())
    .sort((a, b) => a.delayMs - b.delayMs || a.id - b.id)
    .forEach((entry) => {
      if (entry.delayMs <= 0) {
        runSubscriber(entry);
        return;
      }
      const timerId = setTimeout(() => {
        pendingTimers.delete(timerId);
        // Gecikme sırasında uygulama tekrar arka plana gittiyse istek atma.
        if (typeof document !== "undefined" && document.hidden) return;
        if (!subscribers.has(entry.id)) return;
        runSubscriber(entry);
      }, entry.delayMs);
      pendingTimers.add(timerId);
    });
};

const onVisibility = () => {
  if (document.hidden) markInactive();
  else emitResume();
};

const onFocus = () => emitResume();
const onBlur = () => markInactive();
const onPageShow = (event) => {
  // bfcache dönüşünde visibility sinyali gelmeyebilir.
  if (event?.persisted) emitResume({ force: true });
  else emitResume();
};
const onPageHide = () => markInactive();

const installListeners = () => {
  if (listenersInstalled || typeof window === "undefined" || typeof document === "undefined") return;
  listenersInstalled = true;
  window.addEventListener("focus", onFocus);
  window.addEventListener("blur", onBlur);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibility);
};

const removeListenersIfUnused = () => {
  if (!listenersInstalled || subscribers.size > 0 || typeof window === "undefined" || typeof document === "undefined") return;
  window.removeEventListener("focus", onFocus);
  window.removeEventListener("blur", onBlur);
  window.removeEventListener("pageshow", onPageShow);
  window.removeEventListener("pagehide", onPageHide);
  document.removeEventListener("visibilitychange", onVisibility);
  clearPendingTimers();
  listenersInstalled = false;
  inactiveObserved = false;
  inactiveSince = 0;
};

export const subscribeAppResume = (callback, options = {}) => {
  if (typeof callback !== "function") return () => {};
  const id = nextSubscriberId++;
  const delayMs = Math.max(0, Math.min(1200, Number(options.delayMs) || 0));
  subscribers.set(id, { id, callback, delayMs });
  installListeners();
  return () => {
    subscribers.delete(id);
    removeListenersIfUnused();
  };
};
